
import React from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '@/components/blog/blogData';
import SEOMetadata from '@/components/common/SEOMetadata';

const Sitemap = () => {
  const mainRoutes = [
    { path: '/', label: 'Home', description: 'Cats About Town Tours - NYC\'s premier historical cat walking tours' },
    { path: '/about', label: 'About Us', description: 'Learn about our expert team of feline historians and guides' },
    { path: '/tours', label: 'All Tours', description: 'Browse all available cat history walking tours' },
    { path: '/blog', label: 'Blog', description: 'Stories about NYC\'s historic felines and walking tour experiences' },
    { path: '/gallery', label: 'Gallery', description: 'Photos from our cat-themed walking tours' },
    { path: '/contact', label: 'Contact Us', description: 'Get in touch with questions or special requests' },
    { path: '/booking', label: 'Book a Tour', description: 'Reserve your spot on an upcoming cat history tour' },
    { path: '/press-kit', label: 'Press Kit', description: 'Media resources about our unique cat history tours' },
    { path: '/in-the-news', label: 'In the News', description: 'Media coverage of our distinctive walking tours' }
  ];

  const tourRoutes = [
    { path: '/tours/brooklyn-heights', label: 'Brooklyn Heights Cat Tour', description: 'Discover the cats who shaped Brooklyn Heights history' },
    { path: '/tours/financial-district', label: 'Financial District Cat Tour', description: 'Meet the feline heroes of NYC\'s financial center' },
    { path: '/tours/lower-east-side', label: 'Lower East Side Cat Tour', description: 'Explore immigrant history through its feline companions' },
    { path: '/tours/private-tours', label: 'Private Cat Tours', description: 'Custom cat history tours for groups and special occasions' }
  ];

  // Ensure blogPosts is an array
  const safeBlogs = Array.isArray(blogPosts) ? blogPosts : [];

  return (
    <div className="min-h-screen pt-20">
      <SEOMetadata
        title="Site Map - Find All Pages | Cats About Town Tours"
        description="A complete site map of all pages on Cats About Town Tours website. Find tours, blog posts, gallery, and contact information easily."
        keywords={[
          "site map",
          "website navigation",
          "cat tours NYC",
          "all pages",
          "NYC cat history",
          "tour pages",
          "blog posts",
          "contact information"
        ]}
        canonicalUrl="https://catsabouttowntours.com/sitemap"
        ogImage="/lovable-uploads/a7814c71-1569-43b8-8a9c-e24f811be427.png"
        ogImageAlt="Cats About Town Tours Site Map"
      />

      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-center">Site Map</h1>
        <p className="text-lg text-center mb-12 max-w-2xl mx-auto">
          Use this page to find your way around our website. You'll find links to all major sections and content.
        </p>

        <div className="max-w-4xl mx-auto">
          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4 text-primary">Main Pages</h2>
            <ul className="space-y-4">
              {mainRoutes.map((route) => (
                <li key={route.path} className="transition-all hover:-translate-y-1">
                  <Link 
                    to={route.path}
                    className="block p-4 bg-[#FDF6ED] hover:bg-[#F5EDE0] rounded-md shadow-sm flex justify-between items-center group"
                  >
                    <div>
                      <span className="font-medium text-lg text-primary group-hover:text-accent">{route.label}</span>
                      <p className="text-sm text-gray-600 mt-1">{route.description}</p>
                    </div>
                    <span className="text-accent opacity-0 group-hover:opacity-100 transition-opacity">Visit &rarr;</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4 text-primary">Tour Pages</h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tourRoutes.map((route) => (
                <li key={route.path} className="transition-all hover:-translate-y-1">
                  <Link 
                    to={route.path}
                    className="block p-4 bg-[#F5EDE0] hover:bg-[#EADDCA] rounded-md shadow-sm h-full group"
                  >
                    <span className="font-medium text-accent group-hover:text-primary">{route.label}</span>
                    <p className="text-sm text-gray-600 mt-1">{route.description}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-primary">Blog Posts</h2>
            {safeBlogs.length > 0 ? (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {safeBlogs.map((post) => (
                  <li key={post.id} className="transition-all hover:-translate-y-1">
                    <Link 
                      to={`/blog?post=${post.id}`}
                      className="block p-4 bg-[#FDF6ED] hover:bg-[#F5EDE0] rounded-md shadow-sm h-full group"
                    >
                      <span className="font-medium text-primary group-hover:text-accent">{post.title}</span>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{post.excerpt}</p>
                      <p className="text-xs text-accent mt-2">{post.date} · {post.category}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600 italic">No blog posts available</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Sitemap;
